import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import BulkStockImportModal from "../components/BulkStockImportModal";
import { hasPerm } from "../components/Sidebar";
import { MagnifyingGlass, Warning, Package, XCircle, CaretUp, CaretDown, UploadSimple, PencilSimple, X, Trash } from "@phosphor-icons/react";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "in", label: "In Stock" },
  { key: "low", label: "Low (≤5)" },
  { key: "oos", label: "Out of Stock" },
];

const SORT_OPTIONS = [
  { key: "name", label: "Name" },
  { key: "sku", label: "SKU" },
  { key: "qty", label: "Stock" },
  { key: "price", label: "Price" },
  { key: "updated", label: "Updated" },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-md text-sm";

function EditItemModal({ storeId, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    product_name: item.product_name || "",
    barcode: item.barcode || "",
    hsn: item.hsn || "",
    gst_rate: item.gst_rate ?? 0,
    selling_price: item.selling_price ?? 0,
    mrp: "",
    reorder_level: item.reorder_level ?? 5,
    qty: item.qty ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // MRP lives on the SKU master — fetch current value
  useEffect(() => {
    api.get(`/warehouse/skus?q=${encodeURIComponent(item.sku)}&limit=5`)
      .then((r) => {
        const rows = r.data?.items || r.data || [];
        const m = rows.find((s) => s.sku === item.sku);
        if (m) setForm((f) => ({ ...f, mrp: m.mrp ?? 0 }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.sku]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/store/${storeId}/inventory/${encodeURIComponent(item.sku)}`, {
        product_name: form.product_name,
        barcode: form.barcode,
        hsn: form.hsn,
        gst_rate: parseFloat(form.gst_rate) || 0,
        selling_price: parseFloat(form.selling_price) || 0,
        mrp: form.mrp === "" ? undefined : (parseFloat(form.mrp) || 0),
        reorder_level: parseInt(form.reorder_level) || 0,
        qty: parseFloat(form.qty) || 0,
      });
      onSaved();
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="edit-item-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Edit Item</div>
            <div className="text-xs text-slate-500 font-mono-num">{item.sku}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Product Name">
            <input data-testid="edit-item-name" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Barcode">
              <input data-testid="edit-item-barcode" value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={`${inputCls} font-mono-num`} />
            </Field>
            <Field label="HSN Code">
              <input data-testid="edit-item-hsn" value={form.hsn} onChange={(e) => set("hsn", e.target.value)} className={`${inputCls} font-mono-num`} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GST %">
              <input data-testid="edit-item-gst" type="number" step="0.1" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={`${inputCls} font-mono-num text-right`} />
            </Field>
            <Field label="Offer / Selling Price (₹, incl. GST)">
              <input data-testid="edit-item-price" type="number" step="0.01" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} className={`${inputCls} font-mono-num text-right`} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="MRP (₹, on barcode label)">
              <input data-testid="edit-item-mrp" type="number" step="0.01" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} className={`${inputCls} font-mono-num text-right`} />
            </Field>
            <Field label="Reorder Level">
              <input data-testid="edit-item-reorder" type="number" step="1" value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)} className={`${inputCls} font-mono-num text-right`} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Stock Qty">
              <input data-testid="edit-item-qty" type="number" step="1" value={form.qty} onChange={(e) => set("qty", e.target.value)} className={`${inputCls} font-mono-num text-right`} />
            </Field>
          </div>
          {parseFloat(form.qty) !== (item.qty ?? 0) && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Stock change {item.qty} → {form.qty} will be recorded in the audit log as a manual adjustment.
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="edit-item-save" className="h-10 px-5 bg-slate-900 text-white rounded-md text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Inventory() {
  const { storeId } = useParams();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "inventory.edit");
  const isAdmin = user?.role === "super_admin";
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState("asc");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ skus: 0, units: 0, oos: 0, low: 0, value: 0 });
  const [loading, setLoading] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/store/${storeId}/inventory?q=${encodeURIComponent(q)}&stock_status=${status}&sort=${sort}&direction=${dir}`),
        api.get(`/store/${storeId}/inventory/stats`),
      ]);
      setItems(a.data);
      setStats(b.data);
    } catch (e) {
      // eg 403 for a user without access to this store — show empty state
      setItems([]);
      setStats({ skus: 0, units: 0, oos: 0, low: 0, value: 0 });
    } finally { setLoading(false); }
  }, [storeId, q, status, sort, dir]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (k) => {
    if (sort === k) setDir(dir === "asc" ? "desc" : "asc");
    else { setSort(k); setDir("asc"); }
  };

  const removeItem = async (s) => {
    if (!window.confirm(`Delete "${s.product_name}" (${s.sku}) from this store's inventory?\n\nCurrent stock: ${s.qty} units. The SKU stays in the SKU Master; only this store's stock row is removed. This is recorded in the audit log.`)) return;
    try {
      await api.delete(`/store/${storeId}/inventory/${encodeURIComponent(s.sku)}`);
      load();
    } catch (e) { alert("Delete failed: " + (e.response?.data?.detail || e.message)); }
  };

  const Th = ({ k, children, align = "left" }) => (
    <th className={`px-4 py-3 font-semibold ${align === "right" ? "text-right" : "text-left"}`}>
      <button onClick={() => toggleSort(k)} data-testid={`sort-${k}`} className={`inline-flex items-center gap-1 hover:text-slate-900 ${sort === k ? "text-slate-900" : ""}`}>
        {children}
        {sort === k && (dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </button>
    </th>
  );

  return (
    <Shell>
      <PageHeader
        title="Store Inventory"
        subtitle="Current stock at this store. Low = ≤ reorder · Out of Stock = 0."
        testId="inv-header"
        actions={
          <button
            onClick={() => setBulkImportOpen(true)}
            data-testid="bulk-import-stock-btn"
            className="inline-flex items-center gap-1.5 h-10 px-3 bg-white border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-50"
          >
            <UploadSimple size={14} weight="bold" />Import Stock
          </button>
        }
      />
      <div className="p-8 space-y-4">
        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="stat-skus">
            <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">SKUs</span><Package size={16} className="text-slate-400" /></div>
            <div className="font-display font-bold text-2xl font-mono-num mt-1.5">{stats.skus}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="stat-units">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Units</div>
            <div className="font-display font-bold text-2xl font-mono-num mt-1.5">{stats.units.toLocaleString("en-IN")}</div>
          </div>
          <div className={`bg-white border rounded-md p-4 ${stats.low > 0 ? "border-amber-300" : "border-slate-200"}`} data-testid="stat-low">
            <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Low Stock</span><Warning size={16} className="text-amber-500" /></div>
            <div className={`font-display font-bold text-2xl font-mono-num mt-1.5 ${stats.low > 0 ? "text-amber-600" : ""}`}>{stats.low}</div>
          </div>
          <div className={`bg-white border rounded-md p-4 ${stats.oos > 0 ? "border-red-300" : "border-slate-200"}`} data-testid="stat-oos">
            <div className="flex items-center justify-between"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Out of Stock</span><XCircle size={16} className="text-red-500" /></div>
            <div className={`font-display font-bold text-2xl font-mono-num mt-1.5 ${stats.oos > 0 ? "text-red-600" : ""}`}>{stats.oos}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex bg-white border border-slate-200 rounded-md p-1">
            {STATUS_TABS.map((t) => (
              <button key={t.key || "all"} onClick={() => setStatus(t.key)} data-testid={`status-${t.key || "all"}`} className={`px-3 h-8 text-xs font-semibold rounded-sm uppercase tracking-wider ${status === t.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[240px] max-w-md relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name or barcode…" className="w-full h-10 pl-9 pr-3 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </form>

          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="h-10 px-3 border border-slate-300 bg-white rounded-md text-sm">
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>Sort: {o.label}</option>)}
          </select>
          <button onClick={() => setDir(dir === "asc" ? "desc" : "asc")} data-testid="toggle-dir" className="h-10 px-3 border border-slate-300 bg-white rounded-md text-xs font-semibold uppercase tracking-wider hover:bg-slate-50">{dir === "asc" ? "↑ Asc" : "↓ Desc"}</button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="inv-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <Th k="sku">SKU</Th>
                <Th k="name">Name</Th>
                <th className="text-left px-4 py-3 font-semibold">Barcode</th>
                <Th k="qty" align="right">Stock</Th>
                <th className="text-right px-4 py-3 font-semibold">Reorder</th>
                <th className="text-right px-4 py-3 font-semibold">GST %</th>
                <Th k="price" align="right">Selling</Th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                {(canEdit || isAdmin) && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-400">No items match.</td></tr>}
              {!loading && items.map((s) => {
                const ro = s.reorder_level != null ? Number(s.reorder_level) : 5;
                const badge = s.qty <= 0
                  ? { t: "OOS", c: "bg-red-50 text-red-700 border-red-200" }
                  : s.qty <= ro
                    ? { t: "LOW", c: "bg-amber-50 text-amber-700 border-amber-200" }
                    : { t: "IN", c: "bg-emerald-50 text-emerald-700 border-emerald-200" };
                return (
                  <tr key={s.sku} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono-num text-slate-900">{s.sku}</td>
                    <td className="px-4 py-2">{s.product_name}</td>
                    <td className="px-4 py-2 font-mono-num text-slate-600">{s.barcode || "—"}</td>
                    <td className={`px-4 py-2 text-right font-mono-num font-bold ${s.qty <= 0 ? "text-red-600" : s.qty <= ro ? "text-amber-600" : "text-slate-900"}`}>{s.qty}</td>
                    <td className="px-4 py-2 text-right font-mono-num text-slate-500" data-testid={`reorder-${s.sku}`}>{ro}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{(s.gst_rate || 0).toFixed(1)}</td>
                    <td className="px-4 py-2 text-right font-mono-num text-emerald-700">{inr(s.selling_price)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 border rounded-full text-[9px] uppercase tracking-widest font-bold ${badge.c}`}>{badge.t}</span>
                    </td>
                    {(canEdit || isAdmin) && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {canEdit && (
                          <button onClick={() => setEditItem(s)} data-testid={`edit-item-${s.sku}`} title="Edit item" className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md">
                            <PencilSimple size={14} />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => removeItem(s)} data-testid={`delete-item-${s.sku}`} title="Delete item from this store (admin)" className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md">
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {bulkImportOpen && (
        <BulkStockImportModal
          storeId={storeId}
          onClose={() => setBulkImportOpen(false)}
          onDone={load}
        />
      )}
      {editItem && (
        <EditItemModal
          storeId={storeId}
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); load(); }}
        />
      )}
    </Shell>
  );
}
